import React, { useState } from 'react';
import axios from 'axios';
import { analytics, db } from '../firebaseConfig';
import {
  Timestamp,
  collection,
  doc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  where,
} from 'firebase/firestore';
import BookSearch from './BookSearch';
import closeButton from '../images/close-line-white.svg';
import searchIcon from '../images/search-line.svg';
import clearIcon from '../images/close-line.svg';
import { readingStatus } from '../utils/functions';
import { useTranslation } from '../i18n';
import { logEvent } from 'firebase/analytics';

const Search = ({
  user,
  fetchBooks,
  showNotification,
  updateCurrentlyReading,
}) => {
  const [input, setInput] = useState('');
  const [bookInfo, setBookInfo] = useState(null);
  const [foundBooks, setFoundBooks] = useState([]);
  const [lang, setLang] = useState('');
  const [options, setOptions] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [modalIsOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();

  const [history, setHistory] = useState(
    JSON.parse(localStorage.getItem('searchHistory')),
  );

  const handleInputChange = (event) => {
    setInput(event.target.value);
  };

  const handleLangChange = (e) => {
    setLang(e.target.value);
  };

  const handleOptionsChange = (e) => {
    setOptions(e.target.value);
  };

  const handleSetHistory = (search, options, update) => {
    if (update) {
      if (!search) {
        return false;
      }

      let newEntry = { search, options };
      let storedHistory =
        JSON.parse(localStorage.getItem('searchHistory')) || [];

      // Prevent duplicates
      storedHistory = storedHistory.filter((item) => item.search !== search);

      storedHistory.unshift(newEntry);

      if (storedHistory.length > 10) {
        storedHistory.splice(10);
      }

      localStorage.setItem('searchHistory', JSON.stringify(storedHistory));
      setHistory(storedHistory);
    } else {
      localStorage.removeItem('searchHistory');
      setHistory([]);
    }
  };

  const handleSearch = async (query, opt) => {
    if (!query) {
      return false;
    }
    const resultWrapper = document.getElementById('search-results');
    resultWrapper && resultWrapper.scrollTo(0, 0);

    setLoading(true);
    setFoundBooks([]);
    const searchValue = query || input;

    const apiKey = process.env.REACT_APP_GOOGLE_BOOKS_API_KEY;
    const url = `https://www.googleapis.com/books/v1/volumes`;

    logEvent(analytics, 'book_search', {
      query: searchValue,
      options: opt || options,
    });

    try {
      const response = await axios.get(url, {
        params: {
          q: `${opt || options}${searchValue}`,
          ...(lang && { langRestrict: lang }),
          maxResults: 10,
          key: apiKey,
        },
      });

      setFoundBooks(response.data.items);
    } catch (err) {
      showNotification(t.searchError);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBook = async (status, dateFinished) => {
    setSaving(true);
    if (bookInfo) {
      try {
        const userBook = {
          userID: user.uid,
          bookID: bookInfo.id,
          title: bookInfo.volumeInfo.title || '',
          subtitle: bookInfo.volumeInfo.subtitle || '',
          author_name: bookInfo.volumeInfo.authors || [],
          number_of_pages: parseInt(bookInfo.volumeInfo.pageCount) || NaN,
          publisher: bookInfo.volumeInfo.publisher || '',
          publish_date: bookInfo.volumeInfo.publishedDate || '',
          cover_url:
            [
              bookInfo.volumeInfo.imageLinks?.thumbnail + `&fife=w800`,
              bookInfo.volumeInfo.imageLinks?.smallThumbnail,
            ] || '',
          isbn: bookInfo.volumeInfo.industryIdentifiers[0]?.identifier || null,
          status: status,
          starred: false,
          review: '',
          rating: '',
          dateStarted: '',
          dateFinished: dateFinished || '',
          createdAt: serverTimestamp(),
          lang: bookInfo.volumeInfo.language,
          categories: bookInfo.volumeInfo.categories || [],
          description: bookInfo.volumeInfo.description || '',
        };

        const userBooksRef = collection(db, 'UserBooks');

        const q = query(
          userBooksRef,
          where('userID', '==', userBook.userID),
          where('bookID', '==', userBook.bookID),
        );
        const querySnapshot = await getDocs(q);

        if (querySnapshot.empty) {
          await setDoc(doc(userBooksRef), userBook);
        } else {
          setSaving(false);
          showNotification(`${t.bookexists}`);
          closeModal();
          return false;
        }

        if (status === 'reading' && !isNaN(bookInfo.volumeInfo.pageCount)) {
          try {
            updateCurrentlyReading(
              {
                bookID: bookInfo.id,
                title: bookInfo.volumeInfo.title || '',
                author_name: bookInfo.volumeInfo.authors || [],
                isbn:
                  bookInfo.volumeInfo.industryIdentifiers[0].identifier || null,
                number_of_pages: parseInt(bookInfo.volumeInfo.pageCount),
                publish_date: bookInfo.volumeInfo.publishedDate || '',
                publisher: bookInfo.volumeInfo.publisher || '',
                cover_url:
                  [
                    bookInfo.volumeInfo.imageLinks?.thumbnail,
                    bookInfo.volumeInfo.imageLinks?.smallThumbnail,
                  ] || '',
                pages: 0,
                userID: user.uid,
              },
              user,
              true,
            );
          } catch (error) {
            console.error('Error updating User: ', error);
          }
        }
        showNotification(
          `${bookInfo.volumeInfo.title} ${t.savedto} ${readingStatus(status)}!`,
        );
        logEvent(analytics, 'book_saved_to_list', {
          status: status,
        });
        fetchBooks();
      } catch (err) {
        console.error(err);
        showNotification(`${t.saveError}`);
      } finally {
        setSaving(false);
        closeModal();
      }
    }
  };

  const getOptionsLabel = (opt) => {
    let label = '';
    switch (opt) {
      case 'intitle:':
        label = t.title;
        break;
      case 'inauthor:':
        label = t.author;
        break;
      case 'subject:':
        label = t.subject;
        break;
      case 'publisher:':
        label = t.publisher;
        break;
      case 'isbn:':
        label = t.isbn;
        break;

      default:
        label = t.everything;
        break;
    }
    return label;
  };

  function openModal() {
    setIsOpen(true);
  }

  function closeModal() {
    setIsOpen(false);
  }

  return (
    <main>
      <BookSearch
        handleInputChange={handleInputChange}
        lang={lang}
        handleLangChange={handleLangChange}
        handleSearch={handleSearch}
        options={options}
        handleOptionsChange={handleOptionsChange}
        handleSetHistory={handleSetHistory}
        openModal={openModal}
        input={input}
        loading={loading}
        saving={saving}
        bookInfo={bookInfo}
        user={user}
        fetchBooks={fetchBooks}
        showNotification={showNotification}
        setBookInfo={setBookInfo}
        foundBooks={foundBooks}
        updateCurrentlyReading={updateCurrentlyReading}
      />

      {!loading && history && history.length > 0 && (
        <div className="card history">
          <div className="history-header">
            <div className="pt-65">{t.searchhistory}</div>
            <div className="separator"></div>
          </div>
          {history.map((h, index) => {
            const isObject = typeof h === 'object';
            const searchValue = isObject ? h.search : h;
            const optionsValue = isObject ? h.options : '';

            const handleClick = () => {
              setInput(searchValue);
              setOptions(optionsValue);
              handleSearch(searchValue, optionsValue);
            };

            return (
              <div
                key={`${h}-${index}`}
                className="history-item"
                onClick={handleClick}
              >
                <img
                  src={searchIcon}
                  height={24}
                  width={24}
                  alt="Search icon"
                />
                <span>{searchValue}</span>
                {isObject && (
                  <span className="pt-65">
                    [{getOptionsLabel(optionsValue)}]
                  </span>
                )}
              </div>
            );
          })}
          <div className="history-header">
            <div className="separator"></div>
            <div className="history-clear pt-65">
              <button
                className="btn btn-sm btn-borderless fs-s"
                onClick={() => handleSetHistory('', '', false)}
              >
                <img
                  className="icon"
                  src={clearIcon}
                  height={16}
                  width={16}
                  alt="Clear icon"
                />
                {t.clearhistory}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalIsOpen && (
        <div className="overlay">
          <div className="buttons-wrapper">
            <div className="buttons-header">
              <span>{t.savetolist}</span>
              <button
                className="btn-icon"
                onClick={closeModal}
                disabled={saving}
              >
                <img
                  className="icon"
                  src={closeButton}
                  width={24}
                  height={24}
                  alt="Close icon"
                />
              </button>
            </div>
            <div className="separator"></div>
            <div className="buttons">
              <button
                className="btn btn-secondary"
                onClick={() => handleSaveBook('reading')}
                disabled={saving}
              >
                {t.currentlyreading}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => handleSaveBook('wantToRead')}
                disabled={saving}
              >
                {t.wanttoread}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => handleSaveBook('read', Timestamp.now())}
                disabled={saving}
              >
                {t.haveread}
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
};

export default Search;
